import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { updateQuantityRequest, removeFromCartRequest, fetchCartRequest, clearCartRequest } from '../../modules/shop/actions';
import { message, Skeleton } from 'antd';
import { DeleteOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';

export default function CartPage() {
  const { _id: cartId, items, total, loading: cartLoading } = useSelector(state => state.shop.cart);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [selectedItemIds, setSelectedItemIds] = useState([]);

  useEffect(() => {
    dispatch(fetchCartRequest());
  }, [dispatch]);

  // Sync selected items when items change (e.g. after delete)
  useEffect(() => {
    if (items) {
      setSelectedItemIds(prev => prev.filter(id => items.some(item => item._id === id)));
    }
  }, [items]);

  if (cartLoading && (!items || items.length === 0)) {
    return (
      <div className="w-full min-h-screen bg-[#fcfdfe] pt-12 pb-16 text-left">
        <div className="max-w-[1263px] mx-auto px-4 sm:px-6 lg:px-8">
          <Skeleton.Input active size="large" style={{ width: 200, marginBottom: 32 }} />
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2 flex flex-col gap-4">
              {[1, 2, 3].map(i => (
                <div key={i} className="flex gap-4 p-4 bg-white rounded-lg border border-gray-100 shadow-sm items-center">
                  <Skeleton.Image active style={{ width: 96, height: 96 }} className="rounded-md" />
                  <div className="flex-1">
                    <Skeleton active title={{ width: '60%' }} paragraph={{ rows: 1, width: '30%' }} />
                  </div>
                </div>
              ))}
            </div>
            <div className="bg-white p-6 rounded-lg border border-gray-100 shadow-sm h-fit">
              <Skeleton active paragraph={{ rows: 4 }} />
              <div className="mt-6">
                <Skeleton.Button active block size="large" style={{ height: 48 }} />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!items || items.length === 0) {
    return (
      <div className="w-full min-h-screen bg-[#fcfdfe] pt-20 pb-16 text-center">
        <h2 className="text-2xl font-bold mb-4">{t("text_empty_cart")}</h2>
        <button onClick={() => navigate('/product')} className="text-[#ed0a3f] hover:underline">{t("text_continue_shopping")}</button>
      </div>
    );
  }

  const isAllSelected = selectedItemIds.length === items.length && items.length > 0;

  const handleSelectAll = () => {
    if (isAllSelected) {
      setSelectedItemIds([]);
    } else {
      setSelectedItemIds(items.map(item => item._id));
    }
  };

  const handleSelectItem = (id) => {
    setSelectedItemIds(prev => 
      prev.includes(id) ? prev.filter(itemId => itemId !== id) : [...prev, id]
    );
  };

  const handleDeleteSelected = () => {
    if (selectedItemIds.length === 0) {
      message.warning(t('text_select_item_to_delete'));
      return;
    }
    
    if (isAllSelected && cartId) {
      dispatch(clearCartRequest(cartId));
    } else {
      // Dispatch remove for each selected item
      selectedItemIds.forEach(id => {
        dispatch(removeFromCartRequest(id));
      });
    }
  };

  return (
    <div className="w-full min-h-screen bg-[#fcfdfe] pt-12 pb-16 text-left">
      <div className="max-w-[1263px] mx-auto px-4 sm:px-6 lg:px-8">
        <h1 className="text-3xl font-extrabold text-gray-900 mb-8">{t("text_your_cart")}</h1>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 flex flex-col gap-4">
            
            {/* Action Bar */}
            {/* <div className="flex items-center justify-between p-4">
              <div className="flex items-center gap-3">
                <input 
                  type="checkbox" 
                  checked={isAllSelected}
                  onChange={handleSelectAll}
                  className="w-5 h-5 cursor-pointer accent-[#e80d44]"
                />
                <span className="font-medium text-gray-700">Select all</span>
              </div>
              <button 
                onClick={handleDeleteSelected}
                className="px-4 py-1.5 text-sm font-medium border border-gray-300 rounded-md hover:bg-red-50 hover:text-red-600 hover:border-red-200 transition-colors"
              >
                Delete
              </button>
            </div> */}

            {items.map(item => (
              <div key={item._id} className="flex gap-3 sm:gap-4 p-3 sm:p-4 bg-white rounded-lg border border-gray-100 shadow-sm sm:items-center relative pr-10 sm:pr-4">
                {/* <input 
                  type="checkbox"
                  checked={selectedItemIds.includes(item._id)}
                  onChange={() => handleSelectItem(item._id)}
                  className="w-5 h-5 cursor-pointer accent-[#e80d44]"
                /> */}
                <img src={item.productImages?.[0]} alt={item.productName} className="w-20 h-20 sm:w-24 sm:h-24 object-cover rounded-md bg-gray-50 shrink-0" />
                <div className="flex-1 flex flex-col sm:flex-row sm:items-center min-w-0 gap-2 sm:gap-4">
                  <div className="flex-1 min-w-0">
                    <h3 className="font-bold text-gray-900 truncate sm:whitespace-normal">{item.productName}</h3>
                    <div className="text-[#ed0a3f] font-bold mt-1 sm:mt-2">¥{item.unitPrice?.toFixed(2)}</div>
                  </div>
                  <div className="flex items-center gap-3 shrink-0 mt-1 sm:mt-0">
                    <button 
                      onClick={() => item.qty > 1 && dispatch(updateQuantityRequest(item._id, item.qty - 1, item.productId || item.productNo))} 
                      className={`w-7 h-7 sm:w-8 sm:h-8 rounded border flex items-center justify-center ${item.qty > 1 ? 'hover:bg-gray-50 cursor-pointer' : 'text-gray-300 cursor-not-allowed bg-gray-50/50'}`}
                      disabled={item.qty <= 1}
                    >
                      -
                    </button>
                    <span className="font-semibold text-sm sm:text-base">{item.qty}</span>
                    <button onClick={() => dispatch(updateQuantityRequest(item._id, item.qty + 1, item.productId || item.productNo))} className="w-7 h-7 sm:w-8 sm:h-8 rounded border flex items-center justify-center hover:bg-gray-50">+</button>
                  </div>
                </div>
                <button onClick={() => dispatch(removeFromCartRequest(item._id))} className="absolute right-1 top-1/2 -translate-y-1/2 sm:static sm:translate-y-0 text-red-500 text-lg transition-colors p-2 sm:p-0 sm:ml-4 shrink-0"><DeleteOutlined /></button>
              </div>
            ))}
          </div>
          <div className="bg-white p-6 rounded-lg border border-gray-100 shadow-sm h-fit">
            <h3 className="text-xl font-bold mb-6 border-b pb-4">{t("text_order_summary")}</h3>
            <div className="flex justify-between mb-4">
              <span className="text-gray-600">{t("text_subtotal")}</span>
              <span className="font-semibold">¥{total?.toFixed(2)}</span>
            </div>
            {/* <div className="flex justify-between mb-6">
              <span className="text-gray-600">船运</span>
              <span className="font-semibold text-green-600">免费</span>
            </div> */}
            <div className="flex justify-between mb-8 border-t pt-4 text-lg font-bold">
              <span>{t("text_total")}</span>
              <span className="text-[#ed0a3f]">¥{total?.toFixed(2)}</span>
            </div>
            <button 
              onClick={() => navigate('/checkout')}
              className="w-full py-3 bg-gradient-to-r from-[#ed0a3f] to-[#ed0a3f] text-white font-bold rounded shadow hover:opacity-90 transition-opacity"
            >
              {t("text_proceed_checkout")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
